import { useState, useCallback, useEffect, useId } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Paperclip,
  Upload,
  Smartphone,
  Download,
  Trash2,
  Sparkles,
  FileText,
  Loader2,
  Check,
} from "lucide-react";
import { Base64UploadButton } from "@/components/Base64UploadButton";
import { SendToPhoneDialog } from "@/components/SendToPhoneDialog";
import { AIExtractionReviewModal, type ExtractedData } from "@/components/AIExtractionReviewModal";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { SAGE } from "@shared/brand";

export interface DocumentItem {
  id: string;
  filename: string;
}

export interface ExtractionField {
  name: string;
  extractionKey: string;
}

export interface DocumentSectionProps {
  notes: string;
  onNotesChange: (value: string) => void;
  onNotesBlur?: () => void;
  notesPlaceholder?: string;
  /** Optional save-status shown beside the Notes label so auto-save is visible. */
  saveStatus?: "idle" | "saving" | "saved";
  documents: DocumentItem[];
  loadingDocs?: boolean;
  readOnly?: boolean;
  hideDocuments?: boolean;
  clientId?: string;
  /**
   * Omit to suppress the Send-to-Phone affordance. "personal_info" was removed s167
   * (D-164.14 bug 1) — its server handlers were dead and 500'd on call.
   */
  targetType?: "checklist_item" | "digital_asset";
  targetId?: string;
  targetName?: string;
  /** Upload one file via the server-side base64 attach route (s105). Resolves on success. */
  onUploadFile: (file: File) => Promise<void>;
  onDownloadDocument: (docId: string) => void;
  onDeleteDocument: (docId: string) => void;
  downloadEndpoint: string;
  analysisType?: "financial" | "digital_asset";
  /** When provided, uses dynamic field-based AI extraction instead of fixed analysisType extraction */
  extractionFields?: ExtractionField[];
  onAnalysisComplete?: (docId: string, data: ExtractedData) => void;
}

// AI analysis now supports Word/Excel/CSV/text too — the server converts Office docs to
// text/CSV before Gemini (s105 DB-1). Legacy binary .doc is the one exception (mammoth
// reads .docx only), so it stays unsupported.
function isAISupported(filename: string): boolean {
  const ext = filename.toLowerCase().split('.').pop() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'docx', 'xls', 'xlsx', 'csv', 'txt'].includes(ext);
}

function getAIUnsupportedReason(filename: string): string | null {
  if (isAISupported(filename)) return null;
  const ext = filename.toLowerCase().split('.').pop() || '';
  if (ext === 'doc') {
    return "AI analysis isn't available for legacy .doc files — save as .docx and re-upload";
  }
  return "AI analysis only available for PDF, Word, Excel, CSV, images, and text files";
}

export function DocumentSection({
  notes,
  onNotesChange,
  onNotesBlur,
  notesPlaceholder = "Add notes...",
  saveStatus = "idle",
  documents,
  loadingDocs = false,
  readOnly = false,
  hideDocuments = false,
  clientId,
  targetType,
  targetId,
  targetName,
  onUploadFile,
  onDownloadDocument,
  onDeleteDocument,
  downloadEndpoint,
  analysisType = "financial",
  extractionFields,
  onAnalysisComplete,
}: DocumentSectionProps) {
  const { toast } = useToast();
  
  const uid = useId();
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [documentPreview, setDocumentPreview] = useState<string | undefined>(undefined);
  const [analyzingDocId, setAnalyzingDocId] = useState<string | null>(null);

  const analyzeFlight = useSingleFlight();

  const analyzeMutation = useMutation({
    mutationFn: async ({ docId, downloadUrl }: { docId: string; downloadUrl: string }) => {
      const body: Record<string, unknown> = { imageUrl: downloadUrl };

      // Always use field-based extraction; extractionFields defaults to empty array (generic fallback)
      body.fields = extractionFields ?? [];

      const res = await apiRequest("POST", "/api/ai/analyze-document", body);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Analysis failed");
      }
      const raw = await res.json();

      // Convert ExtractedFieldsInfo to ExtractedData shape for the review modal.
      // extractedValues (keyed by extractionKey) → additionalInfo (keyed by field display name).
      if (raw.extractedValues !== undefined) {
        const fields = extractionFields ?? [];
        const keyToName = new Map<string, string>(fields.map((f) => [f.extractionKey, f.name]));
        const additionalInfo: Record<string, string> = {};
        for (const [key, val] of Object.entries(raw.extractedValues as Record<string, string>)) {
          if (val && val.trim()) {
            const label = keyToName.get(key) ?? key;
            additionalInfo[label] = val;
          }
        }
        const converted: ExtractedData = {
          confidence: raw.confidence ?? 0.5,
          rawDescription: raw.rawDescription ?? "Document analyzed",
          additionalInfo: Object.keys(additionalInfo).length > 0 ? additionalInfo : undefined,
        };
        return converted;
      }

      return raw as ExtractedData;
    },
    onSuccess: (data) => {
      setExtractedData(data);
    },
    onError: (error: Error) => {
      toast({
        title: "Analysis failed",
        description: error.message || "Could not analyze document",
        variant: "destructive",
      });
      setAiReviewOpen(false);
      setAnalyzingDocId(null);
    },
  });

  const handleAnalyzeDocument = async (doc: DocumentItem) => {
    try {
      setAnalyzingDocId(doc.id);
      setAiReviewOpen(true);
      setExtractedData(null);
      
      const res = await fetch(`${downloadEndpoint}/${doc.id}/download`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to get document");
      }
      const data = await res.json();
      
      const url = data.url || data.downloadUrl;
      if (!url) {
        throw new Error("Could not get document URL");
      }
      
      setDocumentPreview(url);
      // SINGLE-FLIGHT-PRIMITIVE-1 (s170): reached from an async handler, not directly from a click,
      // so `disabled` on the trigger cannot bound it. Each analysis is a billable Gemini call.
      analyzeFlight.runQuiet(() => analyzeMutation.mutateAsync({ docId: doc.id, downloadUrl: url }));
    } catch (error: unknown) {
      toast({
        title: "Analysis failed",
        description: error instanceof Error ? error.message : "Could not start analysis",
        variant: "destructive",
      });
      setAiReviewOpen(false);
      setAnalyzingDocId(null);
    }
  };

  const handleApproveExtraction = (data: ExtractedData) => {
    const docId = analyzingDocId;
    setAiReviewOpen(false);
    setAnalyzingDocId(null);
    setExtractedData(null);
    setDocumentPreview(undefined);
    toast({
      title: "Data saved",
      description: "The extracted information has been saved.",
    });
    if (docId) {
      onAnalysisComplete?.(docId, data);
    }
  };

  const handleSkipExtraction = () => {
    setAiReviewOpen(false);
    setAnalyzingDocId(null);
    setExtractedData(null);
    setDocumentPreview(undefined);
  };

  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    if (readOnly) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    const itemsArray = Array.from(items);
    for (const item of itemsArray) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const pasted = item.getAsFile();
        if (pasted) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          const filename = `screenshot-${timestamp}.png`;
          // Re-wrap so the file carries a stable name for the server (s105 base64 attach).
          const file = new File([pasted], filename, { type: pasted.type || "image/png" });
          try {
            await onUploadFile(file);
          } catch {
            toast({
              title: "Upload failed",
              description: "Could not upload the pasted image.",
              variant: "destructive",
            });
          }
        }
      }
    }
  }, [readOnly, onUploadFile, toast]);

  useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handlePaste]);

  return (
    <>
      {/* Notes Section */}
      <div className="space-y-2">
        <Label htmlFor={`${uid}-detail-notes`} className="text-sm font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Notes
          {saveStatus === "saving" && (
            <span className="flex items-center gap-1 text-xs font-normal text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving…
            </span>
          )}
          {saveStatus === "saved" && (
            <span className="flex items-center gap-1 text-xs font-normal" style={{ color: SAGE[600] }}>
              <Check className="h-3 w-3" />
              Saved
            </span>
          )}
        </Label>
        {readOnly ? (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted/50 p-4 rounded-lg min-h-[100px]">
            {notes || "No notes added."}
          </p>
        ) : (
          <Textarea
            id={`${uid}-detail-notes`}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            onBlur={onNotesBlur}
            placeholder={notesPlaceholder}
            className="min-h-[100px] resize-y"
            data-testid="textarea-detail-notes"
          />
        )}
      </div>

      {!hideDocuments && (
        <>
          <Separator />

          {/* Documents Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Documents
                {documents.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {documents.length}
                  </Badge>
                )}
              </Label>
              {!readOnly && (
                <div className="flex items-center gap-2">
                  <Base64UploadButton
                    maxNumberOfFiles={5}
                    onUploadFile={onUploadFile}
                    buttonVariant="outline"
                    buttonSize="sm"
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </Base64UploadButton>
                  {clientId && targetId && targetType && (
                    <SendToPhoneDialog
                      clientId={clientId}
                      targetType={targetType}
                      targetId={targetId}
                      targetName={targetName || ""}
                      trigger={
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="button-send-to-phone"
                        >
                          <Smartphone className="h-4 w-4 mr-1" />
                          <span className="text-xs sm:text-sm">Send to Phone</span>
                        </Button>
                      }
                    />
                  )}
                </div>
              )}
            </div>

            {loadingDocs ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading documents...</span>
              </div>
            ) : documents.length > 0 ? (
              <div className="space-y-2">
                {documents.map((doc) => {
                  const aiUnsupportedReason = getAIUnsupportedReason(doc.filename);
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg"
                    >
                      <button
                        className="flex items-center gap-3 overflow-hidden text-left hover:underline cursor-pointer bg-transparent border-none p-0"
                        onClick={() => onDownloadDocument(doc.id)}
                        data-testid={`button-filename-${doc.id}`}
                      >
                        <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                        <span className="text-sm truncate">{doc.filename}</span>
                      </button>
                      <div className="flex items-center gap-1 shrink-0">
                        {!readOnly && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex" data-testid={`tooltip-analyze-${doc.id}`}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => isAISupported(doc.filename) && handleAnalyzeDocument(doc)}
                                  disabled={analyzeMutation.isPending || !isAISupported(doc.filename)}
                                  data-testid={`button-analyze-${doc.id}`}
                                >
                                  <Sparkles className={`h-4 w-4 ${isAISupported(doc.filename) ? 'text-amber-500' : 'text-muted-foreground'}`} />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{aiUnsupportedReason || "Analyze with AI"}</TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button aria-label="Download document"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => onDownloadDocument(doc.id)}
                              data-testid={`button-download-${doc.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Download</TooltipContent>
                        </Tooltip>
                        {!readOnly && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button aria-label="Delete document"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => onDeleteDocument(doc.id)}
                                data-testid={`button-delete-doc-${doc.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
                No documents attached yet.
              </p>
            )}

            {!readOnly && (
              <p className="text-xs text-muted-foreground">
                Tip: Paste a screenshot (Ctrl/Cmd+V) to upload quickly
              </p>
            )}
          </div>
        </>
      )}

      <AIExtractionReviewModal
        open={aiReviewOpen}
        onOpenChange={setAiReviewOpen}
        documentPreviewUrl={documentPreview}
        extractedData={extractedData}
        isAnalyzing={analyzeMutation.isPending}
        onApprove={handleApproveExtraction}
        onSkip={handleSkipExtraction}
        analysisType={analysisType}
      />
    </>
  );
}
